import React from 'react';
import { Box, Card, CardContent, Typography, styled, Grid, Link } from '@mui/material';
import { ClinvarVariantModifier } from '@gen/lda/v1alpha1/genomicquery_pb';
import { getClinicalSignificanceString, getGenotypeString, QueryVariantWithClinvar } from '../../types/genomic';
import LaunchIcon from '@mui/icons-material/Launch';
import ACMGChips from './ACMGChips';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  pageBreakInside: 'avoid',
  boxShadow: 'none',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const VariantTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const VariantDetail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  marginBottom: theme.spacing(1.5),
  '& strong': {
    color: theme.palette.text.primary,
    fontWeight: 500,
    display: 'inline-block',
    width: '160px',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const ExternalLinkItem = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(2),
  '& svg': {
    fontSize: '0.75rem',
    marginLeft: theme.spacing(0.5),
    opacity: 0.5,
  }
}));

const ExternalLinksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const ExternalLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  zIndex: 1000,
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
}));

const DiseaseList: React.FC<{ traitSet: string[] }> = ({ traitSet }) => {
  const maxPrintItems = 3; // Number of items to show in print mode
  // remove 'unknown' from traitSet
  const filteredTraitSet = traitSet.filter(trait => trait !== 'not specified');
  return (
    <Box>
      <SectionTitle variant="subtitle1">Associated Conditions</SectionTitle>
      <Box sx={{
        maxHeight: '200px',
        overflowY: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
        },
        '@media print': {
          maxHeight: 'none',
          overflow: 'visible',
          border: 'none',
          padding: 0,
        }
      }}>
        {filteredTraitSet.map((trait, index) => (
          <VariantDetail 
            key={index}
            sx={{ 
              marginBottom: '4px',
              '&:last-child': {
                marginBottom: 0
              },
              '@media print': {
                display: index < maxPrintItems ? 'block' : 'none'
              }
            }}
          >
            {trait}
          </VariantDetail>
        ))}
        {traitSet.length > maxPrintItems && (
          <VariantDetail
            sx={{
              display: 'none',
              '@media print': {
                display: 'block',
                fontStyle: 'italic'
              }
            }}
          >
            +{traitSet.length - maxPrintItems} more conditions
          </VariantDetail>
        )}
      </Box>
    </Box>
  );
};

interface Props {
  variant: QueryVariantWithClinvar;
  variantType: 'pathogenic' | 'vus' | 'drug-response';
}

const ExternalLinks: React.FC<{ variant: QueryVariantWithClinvar }> = ({ variant }) => {
  const geneSymbol = variant.clinvar.allele.GeneList?.Gene?.[0]?.Symbol;
  const location = variant.clinvar.allele.Location.SequenceLocation[0];
  const position = `${location.Chr}:${location.start}-${location.stop}`;

  return (
    <ExternalLinksContainer>
      <ExternalLinkItem>
        <ExternalLink href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${variant.clinvar.vcvId}`} target="_blank">
          ClinVar <LaunchIcon />
        </ExternalLink>
      </ExternalLinkItem>
      {geneSymbol && (
        <ExternalLinkItem>
          <ExternalLink href={`https://www.omim.org/search?search=${geneSymbol}`} target="_blank">
            <span>OMIM</span> <LaunchIcon />
          </ExternalLink>
        </ExternalLinkItem>
      )}
      <ExternalLinkItem>
        <ExternalLink 
          href={`https://genome.ucsc.edu/cgi-bin/hgTracks?position=chr${position}`} 
          target="_blank" 
        >
          UCSC <LaunchIcon />
        </ExternalLink>
      </ExternalLinkItem>
    </ExternalLinksContainer>
  );
};
export const phredLikelihoodToPercent = (phred: number) => {
  try {
    const dec = 100 - (100 * Math.pow(10, -phred / 10));
    return Math.floor(dec) + "." + dec.toString().split('.')[1].slice(0, 2);
  } catch (e) {
    return "N/A";
  }
}
export const VariantCard: React.FC<Props> = ({ variant }) => {
  return (
    <StyledCard>
      <CardContent>
        <VariantTitle variant="h6">
          {variant.clinvar.allele.Name}
        </VariantTitle>
        <ExternalLinks variant={variant} />

        <Grid container spacing={6}>
          <Grid item xs={6} md={6}>
            <Box>
              {variant.clinvar.allele.OtherNameList && variant.clinvar.allele.OtherNameList?.flatMap(name => name.Name).length > 0 && (
                <VariantDetail>
                  <strong>Alternative Names:</strong>
                  {variant.clinvar.allele.OtherNameList?.flatMap(name => name.Name).join(', ')}
                </VariantDetail>
              )}
              <VariantDetail>
                <strong>VCF Location:</strong>
                chr{variant.chromosome}:{variant.position.toString()}
              </VariantDetail>
              <VariantDetail>
                <strong>Variant Likelihood:</strong>
                {phredLikelihoodToPercent(variant.quality)}%
              </VariantDetail>
              <VariantDetail>
                <strong>Protein Change:</strong>
                {variant.clinvar.allele.HGVSlist.HGVS
                  .filter(hgv => hgv.Type.includes('coding') && hgv.ProteinExpression)
                  .flatMap(hgv => hgv.ProteinExpression!.map(proteinExpression => proteinExpression.Expression))
                  .join(', ')}
              </VariantDetail>
              <VariantDetail>
                <strong>Zygosity:</strong>
                {getGenotypeString(variant.genotype)}
              </VariantDetail>
              <VariantDetail>
                <strong>Clinical Significance:</strong>
                {getClinicalSignificanceString((variant.variantModifier.find(m => m.modifier.case === "clinvar")?.modifier.value as ClinvarVariantModifier).pathogenicity)}
              </VariantDetail>
              <Box sx={{ mt: 2 }}>
                <ACMGChips acmg={variant.clinvar.acmg} />
              </Box>
            </Box>
          </Grid>
          
          {<Grid item xs={6} md={6}>
            <DiseaseList traitSet={variant.clinvar.conditions} />
          </Grid>}
        </Grid>
      </CardContent>
    </StyledCard>
  );
}; 